<template>
  <v-snackbar
    v-model="snackbar"
    location="top"
    :timeout="-1"
    :class="['notification-card', notificationTypeClass, { 'notification-hidden': isHidden }]"
    @mouseenter="showNotification"
    @mouseleave="startHideTimer"
  >
    <div class="card-container">
      <div class="card-left">
        <v-icon color="white" size="24">{{ notificationIcon }}</v-icon>
      </div>
      <div class="card-content">
        <span v-if="notificationMessage" v-html="notificationMessage.message"></span>
      </div>
      <div class="card-right">
        <v-btn
          icon
          variant="text"
          density="compact"
          color="white"
          @click="hideNotification"
        >
          <v-icon size="20">mdi-close-circle</v-icon>
        </v-btn>
      </div>
    </div>
  </v-snackbar>
</template>

<script setup>
import { ref, getCurrentInstance, onMounted, onUnmounted, watch, computed } from 'vue';

const { proxy } = getCurrentInstance();

const snackbar = ref(false);
const notificationMessage = ref(null);
const isHidden = ref(false);
let hideTimer = null;

const notificationIcon = computed(() => {
  if (!notificationMessage.value) return 'mdi-information';
  
  switch (notificationMessage.value.type) {
    case '2':
      return 'mdi-alert';
    case '3':
      return 'mdi-server-off';
    default:
      return 'mdi-information';
  }
});

const notificationTypeClass = computed(() => {
  if (!notificationMessage.value) return 'type-info';
  
  switch (notificationMessage.value.type) {
    case '2':
      return 'type-warning';
    case '3':
      return 'type-maintenance';
    default:
      return 'type-info';
  }
});

function startHideTimer() {
  if (hideTimer) clearTimeout(hideTimer);
  hideTimer = setTimeout(() => {
    isHidden.value = true;
  }, 15000); // 30 secondes
}

function showNotification() {
  isHidden.value = false;
  startHideTimer();
}

watch(snackbar, (newValue) => {
  if (newValue) {
    isHidden.value = false;
    startHideTimer();
  }
});

onMounted(() => {
  getNotificationMessage();
});

onUnmounted(() => {
  if (hideTimer) clearTimeout(hideTimer);
});

async function getNotificationMessage() {
  try {
    const res = await proxy.$api({
      serviceName: "notification",
      methodName: "getActiveNotification"
    });
    
    const currentDate = new Date();
    const startDate = new Date(res.startDate);
    const stopDate = new Date(res.stopDate);

    // Vérifier si la notification a déjà été masquée
    const hiddenNotifications = JSON.parse(localStorage.getItem('hiddenNotifications') || '[]');

    if (currentDate >= startDate && currentDate <= stopDate && !hiddenNotifications.includes(res.id)) {
      notificationMessage.value = res;
      snackbar.value = true;
    }
  } catch (error) {
    console.error('Erreur lors de la récupération de la notification:', error);
  }
}

function hideNotification() {
  if (notificationMessage.value?.id) {
    const hiddenNotifications = JSON.parse(localStorage.getItem('hiddenNotifications') || '[]');
    hiddenNotifications.push(notificationMessage.value.id);
    localStorage.setItem('hiddenNotifications', JSON.stringify(hiddenNotifications));
  }
  snackbar.value = false;
}
</script>

<style scoped>
.notification-card {
  margin-left: 60px !important;
  transition: transform 0.3s ease-in-out !important;
}

.notification-hidden {
  transform: translateY(-90px) !important;
  cursor: pointer;
}

.notification-hidden:hover {
  transform: translateY(0) !important;
}

.notification-card :deep(.v-snackbar__wrapper) {
  border-radius: 8px !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2) !important;
  min-height: 64px !important;
  padding: 0 !important;
}

.type-info :deep(.v-snackbar__wrapper) {
  background: rgba(41, 121, 227, 0.95) !important;
}

.type-warning :deep(.v-snackbar__wrapper) {
  background: rgba(224, 146, 56, 0.95) !important;
}

.type-maintenance :deep(.v-snackbar__wrapper) {
  background: rgba(224, 56, 56, 0.95) !important;
}

.card-container {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 12px 16px;
}

.card-left {
  display: flex;
  align-items: center;
  margin-right: 16px;
}

.card-content {
  flex-grow: 1;
  font-size: 0.875rem;
  line-height: 1.5;
  color: white;
}

.card-right {
  display: flex;
  align-items: center;
  margin-left: 16px;
}

.card-right .v-btn {
  opacity: 0.8;
  transition: opacity 0.2s;
}

.card-right .v-btn:hover {
  opacity: 1;
}
</style>
